import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { leadsAPI, usersAPI } from '../../services/api';

function AddLead() {
  const { token } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [newLead, setNewLead] = useState({
    schoolName: '',
    personName: '', // Contact person name (optional)
    designation: '', // Contact person designation (optional)
    address: '',
    phoneNumber: '',
    email: '',
    assignedTo: '',
    primaryAssignee: '',
    secondaryAssignees: [],
    progressStatus: 'Prospecting',
    statusDescription: '', // Optional description for progress status
    source: 'Website',
    expectedValue: 0,
    rating: 0,
    ratingReason: '',
    progressPlannedEndDate: '', // When current status should end (optional)
    startDate: '', // Overall lead start date (optional)
    endDate: '', // Overall lead conversion target date (optional)
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const LeadSourceOptions = [
    { name: 'Website', value: 'Website' },
    { name: 'Event', value: 'Event' },
    { name: 'Referral', value: 'Referral' },
    { name: 'Direct Outreach', value: 'Direct Outreach' },
    { name: 'Marketing Campaign', value: 'Marketing Campaign' },
  ];

  const LeadStatusOptions = [
    { name: 'Cold Lead', value: 'Cold Lead' },
    { name: 'Prospecting', value: 'Prospecting' },
    { name: 'Qualified', value: 'Qualified' },
    { name: 'Demo Scheduled / Done', value: 'Demo Scheduled / Done' },
    { name: 'Evaluation / Pilot', value: 'Evaluation / Pilot' },
    { name: 'Decision', value: 'Decision' },
    { name: 'Closed Won', value: 'Closed Won' },
    { name: 'Closed Lost', value: 'Closed Lost' },
  ];

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAllUsers();
      setUsers(response.data.users);
    } catch (err) {
      setError('Failed to fetch users');
    }
  };

  const createLead = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await leadsAPI.createLead(newLead);
      setNewLead({
        schoolName: '',
        personName: '',
        designation: '',
        address: '',
        phoneNumber: '',
        email: '',
        assignedTo: '',
        primaryAssignee: '',
        secondaryAssignees: [],
        progressStatus: 'Prospecting',
        statusDescription: '',
        source: 'Website',
        expectedValue: 0,
        rating: 0,
        ratingReason: '',
        progressPlannedEndDate: '',
        startDate: '',
        endDate: '',
      });
      setSuccess('Lead created successfully!');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create lead');
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-6">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-blue-600 bg-clip-text text-transparent">
              Add New Lead
            </h2>
            <p className="text-gray-600 mt-1">Create and assign new leads to team members</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{success}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={createLead} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School Name *</label>
              <input
                type="text"
                placeholder="Enter school name"
                value={newLead.schoolName}
                onChange={(e) => setNewLead({ ...newLead, schoolName: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person Name</label>
              {/* Name of the contact person at the school (optional) */}
              <input
                type="text"
                placeholder="Enter contact person name"
                value={newLead.personName}
                onChange={(e) => setNewLead({ ...newLead, personName: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
              {/* Job title/designation of the contact person (optional) */}
              <input
                type="text"
                placeholder="Enter designation (e.g., Principal, Director)"
                value={newLead.designation}
                onChange={(e) => setNewLead({ ...newLead, designation: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <input
                type="email"
                placeholder="Enter email address"
                value={newLead.email}
                onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
              <input
                type="tel"
                placeholder="Enter phone number"
                value={newLead.phoneNumber}
                onChange={(e) => setNewLead({ ...newLead, phoneNumber: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
              <input
                type="text"
                placeholder="Enter address"
                value={newLead.address}
                onChange={(e) => setNewLead({ ...newLead, address: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lead Source</label>
              <select
                value={newLead.source}
                onChange={(e) => setNewLead({ ...newLead, source: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                {LeadSourceOptions.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Progress Status</label>
              <select
                value={newLead.progressStatus}
                onChange={(e) => setNewLead({ ...newLead, progressStatus: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                {LeadStatusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status Description</label>
              <input
                type="text"
                placeholder="Optional description for current status"
                value={newLead.statusDescription}
                onChange={(e) => setNewLead({ ...newLead, statusDescription: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Primary Assignee</label>
              <select
                value={newLead.primaryAssignee}
                onChange={(e) => setNewLead({ ...newLead, primaryAssignee: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Select Primary Assignee (Optional)</option>
                {users?.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.username}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Assignees</label>
              <div className="border border-gray-300 rounded-xl p-3 max-h-32 overflow-y-auto">
                {users.filter(user => user._id !== newLead.primaryAssignee).map((user) => (
                  <label key={user._id} className="flex items-center space-x-2 mb-2 last:mb-0">
                    <input
                      type="checkbox"
                      checked={newLead.secondaryAssignees.includes(user._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewLead({ ...newLead, secondaryAssignees: [...newLead.secondaryAssignees, user._id] });
                        } else {
                          setNewLead({ ...newLead, secondaryAssignees: newLead.secondaryAssignees.filter(id => id !== user._id) });
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{user.username}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Legacy Assign For Retention</label>
              <select
                value={newLead.assignedTo}
                onChange={(e) => setNewLead({ ...newLead, assignedTo: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Select User (Optional)</option>
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.username}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expected Value *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter expected value"
                value={newLead.expectedValue}
                onChange={(e) => setNewLead({ ...newLead, expectedValue: parseFloat(e.target.value) || 0 })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Initial Rating</label>
              <input
                type="number"
                min="0"
                step="0.1"
                placeholder="Enter initial rating (optional)"
                value={newLead.rating}
                onChange={(e) => setNewLead({ ...newLead, rating: parseFloat(e.target.value) || 0 })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expected End Date of Current Status</label>
              {/* When the current progress status should end */}
              <input
                type="date"
                value={newLead.progressPlannedEndDate}
                onChange={(e) => setNewLead({ ...newLead, progressPlannedEndDate: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lead Start Date</label>
              {/* Overall lead start date - when lead engagement began */}
              <input
                type="date"
                value={newLead.startDate}
                onChange={(e) => setNewLead({ ...newLead, startDate: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lead End Date</label>
              {/* Overall lead target conversion date - when we expect to close the deal */}
              <input
                type="date"
                value={newLead.endDate}
                onChange={(e) => setNewLead({ ...newLead, endDate: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Rating Reason</label>
              <input
                type="text"
                placeholder="Reason for initial rating (optional)"
                value={newLead.ratingReason}
                onChange={(e) => setNewLead({ ...newLead, ratingReason: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Creating Lead...</span>
                </div>
              ) : (
                'Create Lead'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddLead;